import type { EventId, FigureId, SettlementId, WorldDateView } from "../world/types";
import { formatWorldDate, type JournalChannel, type JournalEntry } from "./journal";

/*
 * The other half of the journal: the people, not the news.
 *
 * Phase 4 decision 6 asks for **one anchor of attachment** — a named figure met
 * in the first minutes whose fate rides the simulation. The sim half of that is
 * already true: a caravan's talker is a real roster figure of the town it came
 * from (decision 2), casualties strike the defender's roster, and a `FigureDied`
 * record names them. What the *client* can add is the half the simulation can
 * never hold, and it is the same half `journal/journal.ts` holds for records —
 * **what the player has actually met**.
 *
 * So this is a second store beside the journal, built on the same rule:
 *
 * - **A memory is a reference, never a claim.** A {@link Meeting} is a
 *   {@link FigureId}, the name and home the simulation gave for them, and where
 *   and when the player stood in front of them. Nothing here is inferred and
 *   nothing here is authored: if the client cannot trace a fact to a record or
 *   to a meeting that actually happened, the fact is not shown.
 * - **The client cannot read the future**, so the anchor is emphasis and memory
 *   rather than script. There is no arc to schedule: the arc is the sim's, and
 *   what this does is make sure the player *recognises* it when it walks past —
 *   the same name over the same head two towns later, the town they came from
 *   turning up in a staged reaction, a column on the road out of it, their name
 *   carved on a stone the player stopped to read.
 *
 * # What a tie may say
 *
 * {@link tiesFor} is the whole of the cross-referencing and every branch of it
 * is a join between two things the world said. It never says *died*, because the
 * client cannot know that: what it says is that a stone the player read carries
 * this name, which is true whoever is under it. That distinction is the one to
 * keep if this file grows — a memory that overclaims is worse than no memory,
 * because the whole point of the anchor is that the player can believe it.
 *
 * There is no DOM here. `hud/panels.ts` draws the people view; this is the state
 * and the rules over it, which is what makes it checkable in `dev/selfcheck.ts`
 * (`journal/journal.ts` is written under the same split).
 */

/**
 * Somebody the player has stood in front of and spoken to.
 *
 * By {@link FigureId} and not by name, because a name is a label and the id is
 * the identity the simulation keeps stable for a session (`world/types.ts`).
 * Two people can share a name; the same person met on a road and again in their
 * own street is one entry with {@link Meeting.seen} at two.
 */
export interface Meeting {
  readonly figure: FigureId;

  /** Their name as the simulation spells it, byname and all. Never composed here. */
  readonly name: string;

  /** The settlement they belong to, or `null` when nothing said where they are from. */
  readonly home: SettlementId | null;

  /** What that settlement is called. Empty with no home. */
  readonly homeName: string;

  /** The settlement the *player* was standing in, as they would name it. */
  readonly where: string;

  /** The day of the first meeting, and the day of the most recent one. */
  readonly when: WorldDateView;
  readonly lastSeen: WorldDateView;

  /**
   * How the player met them: talked to in the street, or asked on the road.
   *
   * The journal's own channel vocabulary rather than a second one, so that a
   * meeting and the entry it produced say the same word about the same act.
   */
  readonly how: JournalChannel;

  /** How many separate times they have been spoken to. One on the first meeting. */
  readonly seen: number;
}

/** The same meeting from the inside, where two of its fields still move. */
interface Remembered extends Meeting {
  lastSeen: WorldDateView;
  seen: number;
}

/**
 * One thing that connects somebody the player met to something else the player
 * has seen.
 *
 * The text is the client's, the {@link PersonTie.refs} are the simulation's, and
 * the panel shows both — which is the same bargain every other surface in this
 * client makes (`hud/panels.ts` renders an `EventId` beside everything it
 * claims). A tie with no records is a tie about a meeting, which is the one
 * thing the client is entitled to state on its own authority.
 */
export interface PersonTie {
  readonly text: string;
  readonly refs: readonly EventId[];
}

/** Somebody met, with everything the client can honestly say connects to them. */
export interface RememberedPerson {
  readonly met: Meeting;
  readonly ties: readonly PersonTie[];
}

/**
 * A staged delivery, as the people view needs to see it.
 *
 * Structural rather than imported from `witness/director.ts`, and deliberately:
 * the journal has never depended on the witnessing layer and should not start —
 * it is the thing every other module writes *into*. The director maps its own
 * log onto this shape in one line.
 */
export interface NewsFromAway {
  /** The settlement at the other end of the beat: who told us, who was struck. */
  readonly from: SettlementId | null;

  /** The records the beat carried. */
  readonly refs: readonly EventId[];

  /** Where the player was standing, and the day it landed. */
  readonly where: string;
  readonly when: WorldDateView;
}

/** A refugee column on the roads, as the people view needs to see it. */
export interface ColumnFromAway {
  readonly from: SettlementId;
  readonly refs: readonly EventId[];
  readonly size: number;
}

/** Everything a tie can be drawn from. Read-only, and all of it already on screen. */
export interface Connections {
  /** The journal, which is where "they told me something" lives. */
  readonly entries: readonly JournalEntry[];

  /** What the director has staged this session. */
  readonly news: readonly NewsFromAway[];

  /** The columns currently on the roads. */
  readonly columns: readonly ColumnFromAway[];
}

/**
 * Who the player has met, in the order they met them.
 *
 * Insertion order, like the journal's: a session is a walk, and the first person
 * who spoke to you belongs at the top of the page rather than sorted under
 * whatever the client thinks is most important now.
 */
export class People {
  private readonly list: Remembered[] = [];
  private readonly byFigure = new Map<FigureId, Remembered>();

  /** Everybody met, oldest first. */
  get everyone(): readonly Meeting[] {
    return this.list;
  }

  get size(): number {
    return this.list.length;
  }

  /**
   * Records a meeting, and returns `false` when this was somebody already met.
   *
   * A second meeting is not a second entry: it moves {@link Meeting.lastSeen}
   * and counts up {@link Meeting.seen}, because "I have run into this person
   * three times" is the fact worth keeping and three identical rows is not. The
   * *first* meeting's place and day are kept, since that is the one the player
   * remembers — decision 6's "met in the first minutes".
   *
   * The home is taken from the first meeting that knew one, so a talker met on
   * the road (whose origin the pulse names) does not lose their town if they are
   * later found standing in a street the client cannot attribute.
   */
  meet(meeting: Meeting): boolean {
    const known = this.byFigure.get(meeting.figure);

    if (known !== undefined) {
      known.lastSeen = meeting.lastSeen;
      known.seen += 1;

      return false;
    }

    const fresh: Remembered = { ...meeting, seen: 1, lastSeen: meeting.when };

    this.list.push(fresh);
    this.byFigure.set(meeting.figure, fresh);

    return true;
  }

  /** Whether this figure has been met. The question the world's labels ask. */
  has(figure: FigureId): boolean {
    return this.byFigure.has(figure);
  }

  /** What is remembered about one figure, or `null`. */
  get(figure: FigureId): Meeting | null {
    return this.byFigure.get(figure) ?? null;
  }

  /**
   * What to write over a met person's head.
   *
   * **The whole of the "persistent label" the anchor needs** (decision 6): an
   * unmet body carries the name the simulation gave it, and somebody the player
   * has actually spoken to carries the name *and where they are from* — which is
   * a thing the player learned by meeting them, drawn where they will meet them
   * again. Two towns later, the same plate over the same head is what turns a
   * caravan into somebody they know.
   *
   * The bare name for a figure never met, so a caller can ask about anybody.
   */
  labelFor(figure: FigureId, name: string): string {
    const met = this.byFigure.get(figure);

    if (met === undefined || met.homeName === "") {
      return name;
    }

    return `${met.name} of ${met.homeName}`;
  }

  /** Forgets everybody. Only the dev handle calls it. */
  clear(): void {
    this.list.length = 0;
    this.byFigure.clear();
  }
}

/**
 * Returns everybody met with the connections the client can honestly draw.
 *
 * Rebuilt on each call rather than kept: a session meets a dozen people and the
 * only caller is a panel somebody opened, which is `Journal.byRecord`'s
 * reasoning exactly.
 */
export function remember(people: People, world: Connections): readonly RememberedPerson[] {
  return people.everyone.map((met) => ({ met, ties: tiesFor(met, world) }));
}

/**
 * Everything that ties one met person to something else the player has seen.
 *
 * Four joins, in the order they are worth reading. Each is a *join* rather than
 * an inference: two facts that arrived separately turning out to name the same
 * person or the same town.
 */
export function tiesFor(met: Meeting, world: Connections): readonly PersonTie[] {
  const ties: PersonTie[] = [];

  // 1. What they told the player. The journal's `who` is the speaker label the
  //    simulation composed (`speech`'s name plus byname), and so is the name on
  //    the meeting, so this is an identity rather than a guess.
  const told = world.entries.filter((entry) => entry.via.who === met.name);

  if (told.length > 0) {
    ties.push({
      text: `told you ${told.length === 1 ? "one thing" : `${told.length} things`}`,
      refs: distinct(told.map((entry) => entry.event)),
    });
  }

  // The name as well as the id: a settlement the client never saw named leaves
  // the home name empty (`witness.ts`), and every tie below writes that name
  // into a sentence that would read "word out of  reached Goldenhold".
  if (met.home === null || met.homeName === "") {
    return ties;
  }

  // 2. News out of their town, landing where the player was standing. Only
  //    beats the director actually staged reach here, and the surfaces it stages
  //    are the ones whose other end is where the material came *from* — the
  //    settlement that told this town, or the one a column fled
  //    (`witness/director.ts`).
  for (const news of world.news) {
    if (news.from !== met.home) {
      continue;
    }

    ties.push({
      text: `word out of ${met.homeName} reached ${news.where}, ${formatWorldDate(news.when)}`,
      refs: news.refs,
    });
  }

  // 3. People on the road out of their town, which is the simulation saying the
  //    place they came from emptied.
  for (const column of world.columns) {
    if (column.from !== met.home) {
      continue;
    }

    ties.push({
      text: `${column.size} ${column.size === 1 ? "person" : "people"} on the road out of ${met.homeName}`,
      refs: column.refs,
    });
  }

  // 4. A stone the player stopped and read that carries this name.
  //
  //    **It does not say they are dead**, and the wording is the whole of the
  //    honesty: an inscription is the simulation's own text and a name is not a
  //    unique key, so what the client can state is that the two strings match.
  //    A player reading "a stone at Eldmoor carries this name" beside a grave
  //    they read this morning draws the conclusion themselves, which is the
  //    right division of labour — and the record id is right there if they want
  //    to check it in the trace view.
  const stones = world.entries.filter(
    (entry) => entry.via.channel === "grave" && entry.note.includes(met.name),
  );

  if (stones.length > 0) {
    ties.push({
      text: "a stone you read carries this name",
      refs: distinct(stones.map((entry) => entry.event)),
    });
  }

  return ties;
}

/**
 * Renders a meeting the way the journal renders a provenance: "met at
 * Goldenhold, year 153 day 4".
 *
 * Beside `provenanceOf` rather than inside it, because a meeting is not an entry
 * — it has no record and no note — but it reads in the same voice, and the two
 * lines sit next to each other in the same panel.
 */
export function meetingOf(met: Meeting): string {
  const first = `${verbFor(met.how)} ${met.where}, ${formatWorldDate(met.when)}`;

  if (met.seen < 2) {
    return first;
  }

  return `${first} · spoken to ${met.seen} times, last ${formatWorldDate(met.lastSeen)}`;
}

/** How the player came to meet somebody through a channel. */
function verbFor(how: JournalChannel): string {
  // A caravan is met on the road out of somewhere; everybody else is met where
  // they were standing. The other three channels cannot produce a meeting —
  // a board and a grave are not people, and a refugee column is a *population*
  // rather than a person (history §8.4), which is why `speakGroup` names no
  // figure and nothing here can remember one.
  return how === "caravan" ? "met on the road at" : "met at";
}

/** The distinct records in a list, in the order they first appear. */
function distinct(events: readonly EventId[]): readonly EventId[] {
  const seen: EventId[] = [];

  for (const event of events) {
    if (!seen.includes(event)) {
      seen.push(event);
    }
  }

  return seen;
}
